import { cx } from "classix"
import { CSSProperties, useCallback, useEffect, useRef, useState } from "react"
import { debounce } from "underscore"

import { type InputFieldProps, InputField } from "~/shared/ui/kit/components/InputField"
import { useInputField } from "~/shared/ui/kit/hooks/useInputField"

import { Loading } from "../Loading"

/**
 * Хук используемый с `InputField`, а также используемый внутри `DebouncedInputField`.
 * Передаваемый обработчик `onChange` будет вызываться при изменении поля с указанной задержкой `delay`.
 * @param initialValue начальное значение поля
 * @param onChange обработчик, вызываемый при изменениях с указанной задержкой `delay`
 * @param delay задержка
 * @returns возвращает массив, в котором: 1-ый элемент - свойства, передаваемые `InputField`; 2-ой элемент - установщик значения поля; 3-ий элемент - флаг, который равен true во время изменения поля
 * @hook
 * @category Hook
 */
export function useDebouncedInputField(
	initialValue: string | number,
	onChange: NonNullable<InputFieldProps["onChange"]>,
	delay: number
) {
	const [enterFlag, setEnterFlag] = useState(false)
	const firstRenderRef = useRef(true)

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const setValueDebounced = useCallback(
		debounce((value) => {
			onChange?.(value)
			setEnterFlag(false)
		}, delay),
		[delay, onChange, setEnterFlag]
	)

	const [textField, setTextField] = useInputField(initialValue)

	useEffect(() => {
		if (firstRenderRef.current) {
			firstRenderRef.current = false
			return
		}

		setEnterFlag(true)
		setValueDebounced(textField.value)

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [textField.value])

	return [textField, setTextField, enterFlag] as [
		typeof textField,
		typeof setTextField,
		boolean,
	]
}

/**
 * Свойства `DebouncedInputField`
 */
export interface DebouncedTextFieldOwnProps {
	/**
	 * начальное значение поля
	 * @defaultValue ""
	 */
	initValue?: string | number
	/**
	 * обработчик, вызываемый при изменениях с указанной задержкой `delay`
	 */
	onChange: NonNullable<InputFieldProps["onChange"]>
	/**
	 * задержка
	 * @defaultValue 1000
	 */
	delay?: number
	rootStyle?: CSSProperties
	rootClassName?: string
}

export type DebouncedTextFieldProps = DebouncedTextFieldOwnProps &
	Omit<InputFieldProps, keyof DebouncedTextFieldOwnProps>

/**
 * Неконтролируемая обертка `InputField`, в которой `onChange` вызывается при изменении поля с указанной задержкой `delay`
 * @param props
 * @component
 * @category Component
 * @example
 * ```tsx
 * import {DebouncedInputField} from '~/shared/ui/DebouncedInputField';
 *
 * const employees = ["Иванов И.И.", "Петров П.П.", "Васильев В.В."]
 *
 * function Example() {
 *
 *     const [filter, setFilter] = useState("");
 *     const filteredEmployees = useMemo(() => employees
 *         .filter(name => name
 *             .toLowerCase()
 *             .includes(filter.toLowerCase())
 *         ), [filter])
 *
 *     return (
 *         <div>
 *             <DebouncedInputField initValue="" onChange={value => setFilter(value)}/>
 *             {filteredEmployees.map(name => <div>{name}</div>)}
 *         </div>
 *     )
 * }
 * ```
 */
export function DebouncedInputField({
										initValue = "",
										onChange,
										delay = 1000,
										rootClassName,
										rootStyle,
										...inputProps
									}: DebouncedTextFieldProps) {
	const [tf, , enterFlag] = useDebouncedInputField(initValue, onChange, delay)

	return (
		<div
			className={cx("flex", "items-center", "relative", rootClassName)}
			style={rootStyle}
		>
			<InputField {...tf} {...inputProps} />
			{enterFlag && (
				<Loading
					loading
					title=""
					style={{
						position: "absolute",
						right: "0"
					}}
				/>
			)}
		</div>
	)
}
